import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import {
  BsArrowUp,
  BsArrowDown,
  BsTrash,
  BsFiles,
  BsPhone,
  BsLaptop,
  BsType,
  BsImage,
  BsCursor,
  BsArrowsVertical,
  BsCheck2Circle,
  BsCheck2
} from 'react-icons/bs';
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter, FaLink } from 'react-icons/fa';
import { apiFetch, parseJsonSafe, API_URL } from '../utils/api';
import DOMPurify from 'dompurify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const blockTypes = [
  { type: 'heading', label: 'Título', icon: <BsType /> },
  { type: 'text', label: 'Texto', icon: <BsType /> },
  { type: 'bullets', label: 'Viñetas', icon: <BsCheck2Circle /> },
  { type: 'image', label: 'Imagen', icon: <BsImage /> },
  { type: 'button', label: 'Botón', icon: <BsCursor /> },
  { type: 'spacer', label: 'Espacio', icon: <BsArrowsVertical /> },
  { type: 'columns', label: 'Dos columnas', icon: <BsArrowsVertical /> }
];

const innerBlockTypes = blockTypes.filter((b) => b.type !== 'columns');

const createBlock = (type) => {
  const id = `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  if (type === 'heading') {
    return { id, type, props: { text: 'Título de sección', align: 'center', fontSize: 24, color: '#111111' } };
  }
  if (type === 'text') {
    return { id, type, props: { text: 'Añade aquí tu texto descriptivo.', align: 'left' } };
  }
  if (type === 'bullets') {
    return {
      id,
      type,
      props: {
        items: ['Beneficio principal', 'Segundo punto clave', 'Otro detalle importante'],
        align: 'left',
        fontSize: 14,
        fontWeight: '400',
        fontFamily: 'Arial, sans-serif',
        color: '#111111',
        iconBg: '#16a34a',
        iconColor: '#ffffff'
      }
    };
  }
  if (type === 'image') {
    return { id, type, props: { src: '', alt: '', width: 600, align: 'center' } };
  }
  if (type === 'button') {
    return { id, type, props: { text: 'Llamada a la acción', url: 'https://', align: 'center', background: '#00a884', color: '#ffffff' } };
  }
  if (type === 'spacer') {
    return { id, type, props: { height: 24 } };
  }
  if (type === 'columns') {
    return {
      id,
      type,
      props: {
        columns: [
          { width: 50, blocks: [{ ...createBlock('text'), props: { text: 'Columna 1', align: 'left' } }] },
          { width: 50, blocks: [{ ...createBlock('text'), props: { text: 'Columna 2', align: 'left' } }] }
        ]
      }
    };
  }
  return { id, type: 'text', props: { text: '' } };
};

const EmailEditor = ({ initialValue, onClose, onSave, defaultLayout }) => {
  const [subject, setSubject] = useState(initialValue?.subject || '');
  const [preheader, setPreheader] = useState(initialValue?.preheader || '');
  const [blocks, setBlocks] = useState(initialValue?.blocks || []);
  const [selectedPath, setSelectedPath] = useState(() => {
    const first = initialValue?.blocks?.[0];
    return first ? { id: first.id, parentId: null, columnIdx: null } : null;
  });
  const [previewMode, setPreviewMode] = useState('desktop');
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('token');
  const socialIcons = {
    facebook: <FaFacebook />,
    instagram: <FaInstagram />,
    linkedin: <FaLinkedin />,
    twitter: <FaTwitter />,
    default: <FaLink />
  };
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['link'],
      ['clean']
    ]
  };
  const hasHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ''));
  const escapeHtml = (str = '') => String(str).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[c]));
  const escapeAttr = (str = '') => String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const sanitizeTextHtml = (value) => {
    const raw = String(value || '');
    if (!raw) return '';
    if (!hasHtml(raw)) return escapeHtml(raw).replace(/\n/g, '<br/>');
    let html = raw;
    html = html.replace(/<\/(div|p)>/gi, '<br/>').replace(/<(div|p)(\s[^>]*)?>/gi, '');
    html = html.replace(/<br\s*\/?>/gi, '<br/>');
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'br', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel']
    });
  };

  const normalizeDriveImageUrl = (value) => {
    if (!value) return '';
    const raw = String(value);
    if (!raw.includes('drive.google.com') && !raw.includes('drive.usercontent.google.com')) return raw;
    const match = raw.match(/\/file\/d\/([^/]+)/) || raw.match(/[?&]id=([^&]+)/);
    if (!match) return raw;
    return `/api/uploads/drive/${match[1]}`;
  };
  const toAbsoluteUrl = (url) => {
    if (!url) return '';
    const normalized = normalizeDriveImageUrl(url);
    if (normalized.startsWith('http') || normalized.startsWith('//')) return normalized;
    if (API_URL) return `${API_URL}${normalized}`;
    try {
      return new URL(normalized, window.location.origin).toString();
    } catch (_e) {
      return normalized;
    }
  };

  // Nota: el header/footer se previsualizan en el canvas (no se insertan como bloques).

  const findBlockPath = useCallback((id, tree) => {
    if (!id) return null;
    const top = tree.find((b) => b.id === id);
    if (top) return { id, parentId: null, columnIdx: null };
    for (const parent of tree) {
      if (parent.type !== 'columns') continue;
      const cols = parent.props?.columns || [];
      for (let i = 0; i < cols.length; i += 1) {
        if ((cols[i]?.blocks || []).some((inner) => inner.id === id)) {
          return { id, parentId: parent.id, columnIdx: i };
        }
      }
    }
    return null;
  }, []);

  const resolveTarget = useCallback((target, tree) => {
    const normalized = typeof target === 'string' ? { id: target } : target || {};
    if (!normalized.id) return null;
    if (normalized.parentId !== undefined) {
      if (normalized.parentId === null) {
        return tree.some((b) => b.id === normalized.id) ? { ...normalized, parentId: null } : null;
      }
      const parent = tree.find((b) => b.id === normalized.parentId);
      const col = parent?.props?.columns?.[normalized.columnIdx];
      const exists = (col?.blocks || []).some((b) => b.id === normalized.id);
      return exists ? normalized : null;
    }
    return findBlockPath(normalized.id, tree);
  }, [findBlockPath]);

  const getBlockFromPath = (path, tree) => {
    if (!path?.id) return null;
    if (!path.parentId) return tree.find((b) => b.id === path.id) || null;
    const parent = tree.find((b) => b.id === path.parentId);
    const col = parent?.props?.columns?.[path.columnIdx];
    return col?.blocks?.find((b) => b.id === path.id) || null;
  };

  useEffect(() => {
    const resolved = resolveTarget(selectedPath, blocks);
    if (selectedPath && !resolved) {
      const fallback = blocks[0];
      setSelectedPath(fallback ? { id: fallback.id, parentId: null, columnIdx: null } : null);
    }
  }, [blocks, resolveTarget, selectedPath]);

  const selectedBlock = useMemo(() => {
    const path = resolveTarget(selectedPath, blocks);
    if (!path) return null;
    const found = getBlockFromPath(path, blocks);
    return found ? { ...found, parentId: path.parentId, columnIdx: path.columnIdx } : null;
  }, [blocks, resolveTarget, selectedPath]);

  const handleAddBlock = (type) => {
    const block = createBlock(type);
    setBlocks((prev) => [...prev, block]);
    setSelectedPath({ id: block.id, parentId: null, columnIdx: null });
  };

  const updateBlockProps = (target, patch) => {
    setBlocks((prev) => {
      const path = resolveTarget(target, prev);
      if (!path) return prev;
      if (!path.parentId) {
        return prev.map((b) => (b.id === path.id ? { ...b, props: { ...b.props, ...patch } } : b));
      }
      return prev.map((b) => {
        if (b.id !== path.parentId) return b;
        const columns = b.props?.columns || [];
        const updatedCols = columns.map((col, idx) => {
          if (idx !== path.columnIdx) return col;
          const innerBlocks = (col.blocks || []).map((inner) =>
            inner.id === path.id ? { ...inner, props: { ...inner.props, ...patch } } : inner
          );
          return { ...col, blocks: innerBlocks };
        });
        return { ...b, props: { ...b.props, columns: updatedCols } };
      });
    });
  };

  const moveBlock = (target, direction) => {
    setBlocks((prev) => {
      const path = resolveTarget(target, prev);
      if (!path) return prev;
      if (!path.parentId) {
        const idx = prev.findIndex((b) => b.id === path.id);
        if (idx === -1) return prev;
        const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (targetIdx < 0 || targetIdx >= prev.length) return prev;
        const copy = [...prev];
        const [item] = copy.splice(idx, 1);
        copy.splice(targetIdx, 0, item);
        return copy;
      }
      return prev.map((b) => {
        if (b.id !== path.parentId) return b;
        const cols = b.props?.columns || [];
        const updatedCols = cols.map((col, idx) => {
          if (idx !== path.columnIdx) return col;
          const inner = col.blocks || [];
          const innerIdx = inner.findIndex((blk) => blk.id === path.id);
          const targetIdx = direction === 'up' ? innerIdx - 1 : innerIdx + 1;
          if (innerIdx === -1 || targetIdx < 0 || targetIdx >= inner.length) return col;
          const copy = [...inner];
          const [item] = copy.splice(innerIdx, 1);
          copy.splice(targetIdx, 0, item);
          return { ...col, blocks: copy };
        });
        return { ...b, props: { ...b.props, columns: updatedCols } };
      });
    });
  };

  const duplicateBlock = (target) => {
    setBlocks((prev) => {
      const path = resolveTarget(target, prev);
      if (!path) return prev;
      const newId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      if (!path.parentId) {
        const idx = prev.findIndex((b) => b.id === path.id);
        if (idx === -1) return prev;
        const clone = { ...prev[idx], id: `${prev[idx].type}-${newId}` };
        const copy = [...prev];
        copy.splice(idx + 1, 0, clone);
        return copy;
      }
      return prev.map((b) => {
        if (b.id !== path.parentId) return b;
        const cols = b.props?.columns || [];
        const updatedCols = cols.map((col, idx) => {
          if (idx !== path.columnIdx) return col;
          const inner = col.blocks || [];
          const innerIdx = inner.findIndex((blk) => blk.id === path.id);
          if (innerIdx === -1) return col;
          const clone = { ...inner[innerIdx], id: `${inner[innerIdx].type}-${newId}` };
          const copy = [...inner];
          copy.splice(innerIdx + 1, 0, clone);
          return { ...col, blocks: copy };
        });
        return { ...b, props: { ...b.props, columns: updatedCols } };
      });
    });
  };

  const deleteBlock = (target) => {
    setBlocks((prev) => {
      const path = resolveTarget(target, prev);
      if (!path) return prev;

      if (!path.parentId) {
        const filtered = prev.filter((b) => b.id !== path.id);
        setSelectedPath((current) => {
          if (!current || current.id !== path.id || current.parentId !== null) return current;
          const fallback = filtered[0];
          return fallback ? { id: fallback.id, parentId: null, columnIdx: null } : null;
        });
        return filtered;
      }

      const updated = prev.map((b) => {
        if (b.id !== path.parentId) return b;
        const cols = b.props?.columns || [];
        const updatedCols = cols.map((col, idx) => {
          if (idx !== path.columnIdx) return col;
          return { ...col, blocks: (col.blocks || []).filter((blk) => blk.id !== path.id) };
        });
        return { ...b, props: { ...b.props, columns: updatedCols } };
      });

      setSelectedPath((current) => {
        if (
          !current ||
          current.id !== path.id ||
          current.parentId !== path.parentId ||
          current.columnIdx !== path.columnIdx
        ) {
          return current;
        }
        const parent = updated.find((b) => b.id === path.parentId);
        const col = parent?.props?.columns?.[path.columnIdx];
        const fallback = col?.blocks?.[0];
        if (fallback) return { id: fallback.id, parentId: path.parentId, columnIdx: path.columnIdx };
        return { id: path.parentId, parentId: null, columnIdx: null };
      });

      return updated;
    });
  };

  const handleSave = () => {
    const payload = { subject, preheader, blocks };
    if (onSave) onSave(payload);
    else alert('Diseño guardado (demo).');
  };

  const canvasWidth = previewMode === 'desktop' ? 560 : 360;

  const updateColumns = (blockId, updater) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const cols = Array.isArray(b.props?.columns) ? [...b.props.columns] : [];
        const updatedCols = updater(cols);
        return { ...b, props: { ...b.props, columns: updatedCols } };
      })
    );
  };

  const addInnerBlock = (blockId, colIdx, type) => {
    const inner = createBlock(type);
    updateColumns(blockId, (cols) => {
      const copy = [...cols];
      const baseCol = copy[colIdx] || { width: 50, blocks: [] };
      copy[colIdx] = {
        ...baseCol,
        blocks: [...(baseCol.blocks || []), inner]
      };
      return copy;
    });
    setSelectedPath({ id: inner.id, parentId: blockId, columnIdx: colIdx });
  };

  const isPathSelected = (path) =>
    !!(
      selectedPath &&
      path &&
      selectedPath.id === path.id &&
      selectedPath.parentId === path.parentId &&
      selectedPath.columnIdx === path.columnIdx
    );

  const renderBlock = (block) => {
    const { type, props } = block;
    if (type === 'heading') {
      return (
        <div
          style={{
            background: props.background || 'transparent',
            padding: props.background ? 12 : 0,
            borderRadius: props.background ? 6 : 0,
            marginBottom: 8
          }}
        >
          <h2
            style={{
              fontFamily: props.fontFamily || 'Arial, sans-serif',
              fontSize: props.fontSize || 22,
              fontWeight: props.fontWeight || '700',
              textAlign: props.align || 'left',
              color: props.color || '#111111',
              margin: 0
            }}
          >
            {props.text || 'Título'}
          </h2>
        </div>
      );
    }
    if (type === 'text') {
      const html = sanitizeTextHtml(props.text || '') || sanitizeTextHtml('Texto de ejemplo...');
      return (
        <div
          style={{
            background: props.background || 'transparent',
            padding: props.background ? 12 : 0,
            borderRadius: props.background ? 6 : 0,
            marginBottom: 8
          }}
        >
          <div
            style={{
              fontFamily: props.fontFamily || 'Arial, sans-serif',
              fontSize: props.fontSize || 14,
              fontWeight: props.fontWeight || '400',
              color: props.color || '#111111',
              textAlign: props.align || 'left',
              lineHeight: props.lineHeight || 1.2,
              margin: 0
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      );
    }
    if (type === 'bullets') {
      const items = (props.items || []).filter((item) => String(item || '').trim());
      const align = props.align || 'left';
      const rowAlign = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
      return (
        <div
          style={{
            background: props.background || 'transparent',
            padding: props.background ? 12 : 0,
            borderRadius: props.background ? 6 : 0,
            marginBottom: 8
          }}
        >
          {items.length === 0 ? (
            <div className="text-muted small">Añade elementos de lista.</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: align }}>
              {items.map((item, idx) => (
                <li
                  key={`${item}-${idx}`}
                  style={{
                    marginBottom: 8,
                    textAlign: align
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 10, justifyContent: rowAlign }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: props.iconBg || '#16a34a',
                        color: props.iconColor || '#ffffff',
                        flexShrink: 0,
                        fontSize: 12
                      }}
                    >
                      <BsCheck2 />
                    </span>
                    <span
                      style={{
                        fontFamily: props.fontFamily || 'Arial, sans-serif',
                        fontSize: props.fontSize || 14,
                        fontWeight: props.fontWeight || '400',
                        color: props.color || '#111111',
                        textAlign: align
                      }}
                    >
                      {item}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    if (type === 'image') {
      return (
        <div style={{ textAlign: props.align || 'center', marginBottom: 12 }}>
          {props.src ? (
            <img src={props.src} alt={props.alt || ''} style={{ maxWidth: '100%', width: props.width || 600 }} />
          ) : (
            <div
              style={{
                width: '100%',
                height: 150,
                background: '#f1f3f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999'
              }}
            >
              Imagen (añade una URL)
            </div>
          )}
        </div>
      );
    }
  if (type === 'button') {
    return (
      <div style={{ textAlign: props.align || 'center', marginBottom: 16 }}>
        <button
          type="button"
            style={{
              background: props.background || '#007bff',
              color: props.color || '#ffffff',
              border: 'none',
              borderRadius: props.radius || 4,
              padding: '10px 24px',
              fontSize: props.fontSize || 14,
              fontWeight: props.fontWeight || '600',
              fontFamily: props.fontFamily || 'Arial, sans-serif'
            }}
          >
            {props.text || 'Botón'}
          </button>
        </div>
    );
  }
  if (type === 'spacer') {
    return <div style={{ height: props.height || 24 }} />;
  }
  if (type === 'columns') {
    const cols = props.columns || [];
    return (
      <div className="row" style={{ marginBottom: 12 }}>
        {cols.map((col, idx) => (
          <div key={idx} className="col" style={{ width: `${col.width || 50}%` }}>
            {(col.blocks || []).map((b, innerIdx) => {
              const path = { id: b.id, parentId: block.id, columnIdx: idx };
              return (
                <div
                  key={b.id}
                  className={`mb-2 p-2 ${isPathSelected(path) ? 'border border-primary' : 'border border-transparent'}`}
                  style={{ position: 'relative', cursor: 'pointer', background: '#ffffff', borderRadius: 4 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPath(path);
                  }}
                >
                  <div className="d-flex justify-content-end mb-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-light me-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveBlock(path, 'up');
                      }}
                      disabled={innerIdx === 0}
                    >
                      <BsArrowUp />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light me-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveBlock(path, 'down');
                      }}
                      disabled={innerIdx === (col.blocks || []).length - 1}
                    >
                      <BsArrowDown />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light me-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateBlock(path);
                      }}
                    >
                      <BsFiles />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light text-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteBlock(path);
                      }}
                    >
                      <BsTrash />
                    </button>
                  </div>
                  {renderBlock(b)}
                </div>
              );
            })}
            <div className="d-flex flex-wrap gap-1 mt-2">
              {innerBlockTypes.map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    addInnerBlock(block.id, idx, opt.type);
                  }}
                >
                  <span className="me-1">{opt.icon}</span>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
    return null;
  };

  // eslint-disable-next-line no-unused-vars
  const renderPreviewBlock = (block) => {
    if (!block) return null;
    const { type, props = {} } = block;
    const paddedStyle = {
      background: props.background || 'transparent',
      padding: props.background ? 12 : 0,
      borderRadius: props.background ? 6 : 0
    };

    if (type === 'heading') {
      return (
        <div style={{ ...paddedStyle, marginBottom: 10 }}>
          <h2
            style={{
              fontFamily: props.fontFamily || 'Arial, sans-serif',
              fontSize: props.fontSize || 22,
              fontWeight: props.fontWeight || '700',
              textAlign: props.align || 'left',
              color: props.color || '#111111',
              margin: 0
            }}
          >
            {props.text || 'Título de sección'}
          </h2>
        </div>
      );
    }
    if (type === 'text') {
      const html = sanitizeTextHtml(props.text || '') || sanitizeTextHtml('Texto descriptivo');
      return (
        <div style={{ ...paddedStyle, marginBottom: 10 }}>
          <div
            style={{
              fontFamily: props.fontFamily || 'Arial, sans-serif',
              fontSize: props.fontSize || 14,
              fontWeight: props.fontWeight || '400',
              color: props.color || '#111111',
              textAlign: props.align || 'left',
              lineHeight: props.lineHeight || 1.2,
              margin: 0
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      );
    }
    if (type === 'bullets') {
      const items = (props.items || []).filter((item) => String(item || '').trim());
      const align = props.align || 'left';
      const tableStyle = {
        width: align === 'left' ? '100%' : 'auto',
        display: align === 'left' ? 'table' : 'inline-table'
      };
      return (
        <div style={{ ...paddedStyle, marginBottom: 10, textAlign: align }}>
          {items.length === 0 ? (
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#94a3b8' }}>Lista sin elementos</div>
          ) : (
            <table style={tableStyle} cellPadding="0" cellSpacing="0" role="presentation">
              <tbody>
                {items.map((item, idx) => (
                  <tr key={`${item}-${idx}`}>
                    <td style={{ width: 28, verticalAlign: 'top', paddingBottom: 8 }}>
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: props.iconBg || '#16a34a',
                          color: props.iconColor || '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      >
                        ✓
                      </div>
                    </td>
                    <td
                      style={{
                        fontFamily: props.fontFamily || 'Arial, sans-serif',
                        fontSize: props.fontSize || 14,
                        fontWeight: props.fontWeight || '400',
                        color: props.color || '#111111',
                        textAlign: align,
                        paddingBottom: 8
                      }}
                    >
                      {item}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (type === 'image') {
      const src = toAbsoluteUrl(props.src);
      return (
        <div style={{ textAlign: props.align || 'center', marginBottom: 12 }}>
          {src ? (
            <img src={src} alt={props.alt || ''} style={{ maxWidth: '100%', width: props.width || 600 }} />
          ) : (
            <div
              style={{
                width: '100%',
                height: 160,
                background: '#f1f3f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999'
              }}
            >
              Imagen
            </div>
          )}
        </div>
      );
    }
    if (type === 'button') {
      return (
        <div style={{ textAlign: props.align || 'center', marginBottom: 16 }}>
          <a
            href={props.url || '#'}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-block',
              background: props.background || '#00a884',
              color: props.color || '#ffffff',
              padding: '10px 20px',
              borderRadius: props.radius || 4,
              fontFamily: props.fontFamily || 'Arial, sans-serif',
              fontWeight: props.fontWeight || '600',
              fontSize: props.fontSize || 14,
              textDecoration: 'none'
            }}
          >
            {props.text || 'Botón'}
          </a>
        </div>
      );
    }
    if (type === 'spacer') {
      return <div style={{ height: props.height || 24 }} />;
    }
    if (type === 'columns') {
      const cols = props.columns || [];
      return (
        <div style={{ display: 'flex', gap: 12, flexWrap: previewMode === 'mobile' ? 'wrap' : 'nowrap', marginBottom: 12 }}>
          {cols.map((col, idx) => (
            <div
              key={idx}
              style={{
                flex: `0 0 ${previewMode === 'mobile' ? '100%' : `${col.width || 50}%`}`,
                minWidth: previewMode === 'mobile' ? '100%' : `${col.width || 50}%`
              }}
            >
              {(col.blocks || []).map((inner) => (
                <div key={inner.id} style={{ marginBottom: 8 }}>
                  {renderPreviewBlock(inner)}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleUploadImage = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const res = await apiFetch('/api/uploads/images', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo subir la imagen');
      if (selectedBlock) {
        updateBlockProps(selectedBlock.id, { src: toAbsoluteUrl(data.url) });
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const renderProperties = () => {
    if (!selectedBlock) {
      return <div className="text-muted small">Selecciona un bloque para editar sus propiedades.</div>;
    }
    const { type, id, props } = selectedBlock;

    if (type === 'heading') {
      return (
        <>
          <div className="mb-2">
            <label className="form-label small">Texto</label>
            <input
              className="form-control form-control-sm"
              value={props.text || ''}
              onChange={(e) => updateBlockProps(id, { text: e.target.value })}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small">Fuente y tamaño</label>
            <div className="d-flex gap-2">
              <input
                className="form-control form-control-sm"
                placeholder="Arial, sans-serif"
                value={props.fontFamily || ''}
                onChange={(e) => updateBlockProps(id, { fontFamily: e.target.value })}
              />
              <input
                type="number"
                className="form-control form-control-sm"
                style={{ width: 90 }}
                value={props.fontSize || 24}
                onChange={(e) => updateBlockProps(id, { fontSize: Number(e.target.value) || 24 })}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label small">Peso / color</label>
            <div className="d-flex gap-2">
              <select
                className="form-select form-select-sm"
                value={props.fontWeight || '700'}
                onChange={(e) => updateBlockProps(id, { fontWeight: e.target.value })}
              >
                <option value="400">Normal</option>
                <option value="600">Semibold</option>
                <option value="700">Bold</option>
              </select>
              <input
                type="color"
                className="form-control form-control-sm"
                style={{ width: 70 }}
                value={props.color || '#111111'}
                onChange={(e) => updateBlockProps(id, { color: e.target.value })}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label small">Alineación</label>
            <select
              className="form-select form-select-sm"
              value={props.align || 'center'}
              onChange={(e) => updateBlockProps(id, { align: e.target.value })}
            >
              <option value="left">Izquierda</option>
              <option value="center">Centro</option>
              <option value="right">Derecha</option>
            </select>
          </div>
        </>
      );
    }

    if (type === 'text') {
      return (
        <>
          <div className="mb-2">
            <label className="form-label small">Texto</label>
            <ReactQuill
              theme="snow"
              value={props.text || ''}
              onChange={(val) => updateBlockProps(id, { text: val })}
              modules={quillModules}
              style={{ minHeight: 160 }}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small">Fuente y tamaño</label>
            <div className="d-flex gap-2">
              <input
                className="form-control form-control-sm"
                placeholder="Arial, sans-serif"
                value={props.fontFamily || ''}
                onChange={(e) => updateBlockProps(id, { fontFamily: e.target.value })}
              />
              <input
                type="number"
                className="form-control form-control-sm"
                style={{ width: 90 }}
                value={props.fontSize || 14}
                onChange={(e) => updateBlockProps(id, { fontSize: Number(e.target.value) || 14 })}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label small">Peso / color</label>
            <div className="d-flex gap-2">
              <select
                className="form-select form-select-sm"
                value={props.fontWeight || '400'}
                onChange={(e) => updateBlockProps(id, { fontWeight: e.target.value })}
              >
                <option value="300">Light</option>
                <option value="400">Normal</option>
                <option value="600">Semibold</option>
              </select>
              <input
                type="color"
                className="form-control form-control-sm"
                style={{ width: 70 }}
                value={props.color || '#111111'}
                onChange={(e) => updateBlockProps(id, { color: e.target.value })}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label small">Alineación</label>
            <select
              className="form-select form-select-sm"
              value={props.align || 'left'}
              onChange={(e) => updateBlockProps(id, { align: e.target.value })}
            >
              <option value="left">Izquierda</option>
              <option value="center">Centro</option>
              <option value="right">Derecha</option>
            </select>
          </div>
        </>
      );
    }

    if (type === 'bullets') {
      return (
        <>
          <div className="mb-2">
            <label className="form-label small">Elementos (uno por línea)</label>
            <textarea
              className="form-control form-control-sm"
              rows="6"
              value={(props.items || []).join('\n')}
              onChange={(e) => updateBlockProps(id, { items: e.target.value.split('\n') })}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small">Fuente y tamaño</label>
            <div className="d-flex gap-2">
              <input
                className="form-control form-control-sm"
                placeholder="Arial, sans-serif"
                value={props.fontFamily || ''}
                onChange={(e) => updateBlockProps(id, { fontFamily: e.target.value })}
              />
              <input
                type="number"
                className="form-control form-control-sm"
                style={{ width: 90 }}
                value={props.fontSize || 14}
                onChange={(e) => updateBlockProps(id, { fontSize: Number(e.target.value) || 14 })}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label small">Peso / color</label>
            <div className="d-flex gap-2">
              <select
                className="form-select form-select-sm"
                value={props.fontWeight || '400'}
                onChange={(e) => updateBlockProps(id, { fontWeight: e.target.value })}
              >
                <option value="300">Light</option>
                <option value="400">Normal</option>
                <option value="600">Semibold</option>
              </select>
              <input
                type="color"
                className="form-control form-control-sm"
                style={{ width: 70 }}
                value={props.color || '#111111'}
                onChange={(e) => updateBlockProps(id, { color: e.target.value })}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label small">Icono</label>
            <div className="d-flex gap-2">
              <input
                type="color"
                className="form-control form-control-sm"
                style={{ width: 70 }}
                value={props.iconBg || '#16a34a'}
                onChange={(e) => updateBlockProps(id, { iconBg: e.target.value })}
              />
              <input
                type="color"
                className="form-control form-control-sm"
                style={{ width: 70 }}
                value={props.iconColor || '#ffffff'}
                onChange={(e) => updateBlockProps(id, { iconColor: e.target.value })}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label small">Alineación</label>
            <select
              className="form-select form-select-sm"
              value={props.align || 'left'}
              onChange={(e) => updateBlockProps(id, { align: e.target.value })}
            >
              <option value="left">Izquierda</option>
              <option value="center">Centro</option>
              <option value="right">Derecha</option>
            </select>
          </div>
        </>
      );
    }

    if (type === 'image') {
      return (
        <>
          <div className="mb-2">
            <label className="form-label small">URL imagen</label>
            <input
              className="form-control form-control-sm"
              value={props.src || ''}
              onChange={(e) => updateBlockProps(id, { src: e.target.value })}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small">Alt (texto alternativo)</label>
            <input
              className="form-control form-control-sm"
              value={props.alt || ''}
              onChange={(e) => updateBlockProps(id, { alt: e.target.value })}
            />
          </div>
          <div className="mb-2 d-flex align-items-center gap-2">
            <div>
              <label className="form-label small mb-1">Subir desde dispositivo</label>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                className="form-control form-control-sm"
                onChange={(e) => handleUploadImage(e.target.files?.[0])}
                disabled={uploading}
              />
            </div>
            {uploading && <span className="text-muted small">Subiendo...</span>}
          </div>
          <div className="mb-2">
            <label className="form-label small">Ancho máximo (px)</label>
            <input
              type="number"
              className="form-control form-control-sm"
              value={props.width || 600}
              onChange={(e) => updateBlockProps(id, { width: Number(e.target.value) || 600 })}
            />
          </div>
        </>
      );
    }

    if (type === 'button') {
      return (
        <>
          <div className="mb-2">
            <label className="form-label small">Texto del botón</label>
            <input
              className="form-control form-control-sm"
              value={props.text || ''}
              onChange={(e) => updateBlockProps(id, { text: e.target.value })}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small">URL destino</label>
            <input
              className="form-control form-control-sm"
              value={props.url || ''}
              onChange={(e) => updateBlockProps(id, { url: e.target.value })}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small">Alineación</label>
            <select
              className="form-select form-select-sm"
              value={props.align || 'center'}
              onChange={(e) => updateBlockProps(id, { align: e.target.value })}
            >
              <option value="left">Izquierda</option>
              <option value="center">Centro</option>
              <option value="right">Derecha</option>
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label small">Colores y borde</label>
            <div className="d-flex gap-2">
              <input
                type="color"
                className="form-control form-control-sm"
                style={{ width: 70 }}
                value={props.background || '#007bff'}
                onChange={(e) => updateBlockProps(id, { background: e.target.value })}
              />
              <input
                type="color"
                className="form-control form-control-sm"
                style={{ width: 70 }}
                value={props.color || '#ffffff'}
                onChange={(e) => updateBlockProps(id, { color: e.target.value })}
              />
              <input
                type="number"
                className="form-control form-control-sm"
                style={{ width: 90 }}
                value={props.radius || 4}
                onChange={(e) => updateBlockProps(id, { radius: Number(e.target.value) || 0 })}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label small">Fuente y peso</label>
            <div className="d-flex gap-2">
              <input
                className="form-control form-control-sm"
                placeholder="Arial, sans-serif"
                value={props.fontFamily || ''}
                onChange={(e) => updateBlockProps(id, { fontFamily: e.target.value })}
              />
              <select
                className="form-select form-select-sm"
                value={props.fontWeight || '600'}
                onChange={(e) => updateBlockProps(id, { fontWeight: e.target.value })}
              >
                <option value="500">Medium</option>
                <option value="600">Semibold</option>
                <option value="700">Bold</option>
              </select>
              <input
                type="number"
                className="form-control form-control-sm"
                style={{ width: 90 }}
                value={props.fontSize || 14}
                onChange={(e) => updateBlockProps(id, { fontSize: Number(e.target.value) || 14 })}
              />
            </div>
          </div>
        </>
      );
    }

    if (type === 'spacer') {
      return (
        <div className="mb-2">
          <label className="form-label small">Altura (px)</label>
          <input
            type="number"
            className="form-control form-control-sm"
            value={props.height || 24}
            onChange={(e) => updateBlockProps(id, { height: Number(e.target.value) || 24 })}
          />
        </div>
      );
    }

    if (type === 'columns') {
      return (
        <div className="mb-2">
          <div className="small text-muted mb-1">Ancho columnas (%)</div>
          <div className="d-flex gap-2">
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: 80 }}
              value={props.columns?.[0]?.width || 50}
              onChange={(e) => {
                const cols = [...(props.columns || [])];
                const col = cols[0] || { blocks: [] };
                cols[0] = { ...col, width: Number(e.target.value) || 50, blocks: col.blocks || [] };
                updateBlockProps(id, { columns: cols });
              }}
            />
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: 80 }}
              value={props.columns?.[1]?.width || 50}
              onChange={(e) => {
                const cols = [...(props.columns || [])];
                const col = cols[1] || { blocks: [] };
                cols[1] = { ...col, width: Number(e.target.value) || 50, blocks: col.blocks || [] };
                updateBlockProps(id, { columns: cols });
              }}
            />
          </div>
          <div className="small text-muted mt-1">Ajusta anchos y selecciona bloques dentro de cada columna para editarlos.</div>
        </div>
      );
    }

    return <div className="text-muted small">Bloque sin propiedades editables.</div>;
  };

  return (
    <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" style={{ maxWidth: '1200px' }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Editor de email</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div className="w-50 pe-2">
                <label className="form-label small">Asunto</label>
                <input
                  className="form-control form-control-sm"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Asunto de la campaña"
                />
              </div>
              <div className="w-50 ps-2">
                <label className="form-label small">Preheader</label>
                <input
                  className="form-control form-control-sm"
                  value={preheader}
                  onChange={(e) => setPreheader(e.target.value)}
                  placeholder="Texto previo que se ve en la bandeja"
                />
              </div>
            </div>

            <div className="d-grid" style={{ gap: 16, gridTemplateColumns: '220px 1fr 260px' }}>
              <div style={{ width: '100%' }}>
                <h6 className="mb-2">Bloques</h6>
                <div className="list-group small">
                  {blockTypes.map((b) => (
                    <button
                      key={b.type}
                      className="list-group-item list-group-item-action d-flex align-items-center"
                      onClick={() => handleAddBlock(b.type)}
                    >
                      <span className="me-2">{b.icon}</span> {b.label}
                    </button>
                  ))}
                </div>
                <div className="mt-3">
                  <div className="form-label small mb-1">Vista</div>
                  <div className="btn-group btn-group-sm">
                    <button
                      className={`btn btn-outline-secondary ${previewMode === 'desktop' ? 'active' : ''}`}
                      onClick={() => setPreviewMode('desktop')}
                    >
                      <BsLaptop className="me-1" /> Escritorio
                    </button>
                    <button
                      className={`btn btn-outline-secondary ${previewMode === 'mobile' ? 'active' : ''}`}
                      onClick={() => setPreviewMode('mobile')}
                    >
                      <BsPhone className="me-1" /> Móvil
                    </button>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-center">
                <div
                  style={{
                    width: '100%',
                    maxWidth: canvasWidth,
                    background: '#f5f5f5',
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #ddd'
                  }}
                >
                  <div style={{ background: '#ffffff', padding: 16, borderRadius: 4 }}>
                    {preheader && (
                      <div className="mb-2 text-muted small" style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: 6 }}>
                        {preheader}
                      </div>
                    )}
                    {(() => {
                      const headerLogo = defaultLayout?.headerLogo ? toAbsoluteUrl(defaultLayout.headerLogo) : '';
                      const headerText = (defaultLayout?.headerText || '').trim();
                      const headerLayout = defaultLayout?.headerLayout || 'logo_left';
                      const hasHeader = Boolean(headerLogo || headerText);
                      const hasLogo = Boolean(headerLogo);
                      const hasText = Boolean(headerText);
                      const shouldStack = headerLayout === 'stacked_center';
                      const display = shouldStack ? 'block' : (hasLogo && hasText ? 'flex' : 'block');
                      const textAlign = shouldStack ? 'center' : 'left';
                      if (!hasHeader) return null;
                      return (
                        <div
                          className="mb-3"
                          style={{
                            background: defaultLayout?.headerBg || '#ffffff',
                            padding: '12px 16px',
                            borderRadius: 6,
                            border: '1px solid #e9ecef',
                            display,
                            alignItems: display === 'flex' ? 'center' : undefined,
                            textAlign,
                            gap: 12
                          }}
                        >
                          {hasLogo && (
                            <div style={{ display: 'flex', justifyContent: shouldStack ? 'center' : 'flex-start' }}>
                              <img
                                src={headerLogo}
                                alt="Logo"
                                style={{
                                  maxHeight: 46,
                                  maxWidth: 140,
                                  objectFit: 'contain',
                                  margin: shouldStack ? '0 auto' : '0'
                                }}
                              />
                            </div>
                          )}
                          {hasText && (
                            <div
                              style={{
                                fontWeight: 600,
                                color: '#1f2937',
                                whiteSpace: 'pre-line',
                                marginTop: shouldStack && hasLogo ? 6 : 0
                              }}
                            >
                              {headerText}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {blocks.length === 0 && (
                      <div className="text-muted small text-center py-5">
                        Añade bloques desde la izquierda para comenzar a diseñar tu email.
                      </div>
                    )}
                    {blocks.map((block, idx) => (
                      <div
                        key={block.id}
                        className={`mb-2 p-2 ${
                          selectedPath && (selectedPath.id === block.id || selectedPath.parentId === block.id)
                            ? 'border border-primary'
                            : 'border border-transparent'
                        }`}
                        style={{ position: 'relative', cursor: 'pointer' }}
                        onClick={() => setSelectedPath({ id: block.id, parentId: null, columnIdx: null })}
                      >
                        <div className="d-flex justify-content-end mb-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-light me-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveBlock(block.id, 'up');
                            }}
                            disabled={idx === 0}
                          >
                            <BsArrowUp />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-light me-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveBlock(block.id, 'down');
                            }}
                            disabled={idx === blocks.length - 1}
                          >
                            <BsArrowDown />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-light me-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateBlock(block.id);
                            }}
                          >
                            <BsFiles />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-light text-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBlock(block.id);
                            }}
                          >
                            <BsTrash />
                          </button>
                        </div>
                        {renderBlock(block)}
                      </div>
                    ))}
                    {(() => {
                      const footerText = (defaultLayout?.footerText || '').trim();
                      const unsubscribeUrl = defaultLayout?.unsubscribeUrl || '';
                      const links = Object.entries(defaultLayout?.socialLinks || {}).filter(([, v]) => v);
                      const hasLinks = links.length > 0;
                      const hasFooterText = Boolean(footerText);
                      const hasUnsub = Boolean(unsubscribeUrl);
                      const hasFooter = hasFooterText || hasLinks || hasUnsub;
                      if (!hasFooter) return null;
                      return (
                        <div
                          className="mt-3"
                          style={{
                            background: defaultLayout?.footerBg || '#f8f9fa',
                            padding: '12px 16px',
                            borderRadius: 6,
                            border: '1px solid #e9ecef',
                            color: defaultLayout?.footerTextColor || '#4b5563',
                            textAlign: 'center'
                          }}
                        >
                          {hasFooterText && (
                            <div className="small" style={{ whiteSpace: 'pre-line' }}>
                              {footerText}
                            </div>
                          )}
                          {hasLinks && (
                            <div className={`d-flex gap-3 flex-wrap small align-items-center justify-content-center ${hasFooterText ? 'mt-2' : ''}`}>
                              {links.map(([k, v]) => (
                                <a
                                  key={k}
                                  href={v}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="d-flex align-items-center gap-1"
                                  style={{ textDecoration: 'none', color: defaultLayout?.footerTextColor || '#4b5563' }}
                                >
                                  {socialIcons[k] || socialIcons.default}
                                  <span className="small">{k}</span>
                                </a>
                              ))}
                            </div>
                          )}
                          {hasUnsub && (
                            <div className={hasFooterText || hasLinks ? 'mt-2' : undefined}>
                              <a
                                href={unsubscribeUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="small"
                                style={{ color: defaultLayout?.footerTextColor || '#4b5563' }}
                              >
                                Darse de baja
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div style={{ width: '100%', maxWidth: 260 }}>
                <h6 className="mb-2">Propiedades</h6>
                {renderProperties()}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>
            <button className="btn btn-primary" onClick={handleSave}>Guardar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailEditor;
